import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, Col, Form, Input, Modal, Row } from 'antd';
import { LockOutlined, UnlockOutlined, UserOutlined } from '@ant-design/icons';
import { Catcher, LoadingApiByIds, makeRuleEmail, makeRulePassword } from '../../';
// import sslImage from '../../../static/img/ssl.png';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { GoGetSslLogo } from './GoGetSslLogo';
import { TermsFormFields } from './TermsFormFields';
import { FieldData } from 'rc-field-form/es/interface';
// import { appConfig } from '../../../settings';
import { ILoginRequest } from '../../../REST';
import {
    AppStatus,
    IAntDFormChangeFieldName,
    IPOSTUser2FAEnableResponses,
    IValidationField,
    NotificationType,
} from '../../../interfaces';
import {
    addNotification,
    apiMessagesDeleteMessageByFieldName,
    authLoginUser,
    getAuthAcceptanceDocuments,
    getProfileInfo,
    postUser2FAEnableStep1,
    postUser2FAEnableStep2,
    removeUiNotificationByMessage,
    setAuthAppStatus,
} from '../../../middleware';
import { tB, tF, tP, tT } from '../../../translate';
import { Form2FAQRCode } from '../../User/Components';
import { Form2FACodeConfirm } from '../../User/Components/Form2FACodeConfirm';
import ViewStyles from '../../styles/m_viewStyles.less';
import Styles from '../m_styles.less';

export const LoginForm: React.FC = observer(() => {
    const [form] = Form.useForm();
    const [initialValues] = useState({ email: '', password: '', remember: false });
    const [validatedFields] = useState(['email', 'password']);
    const [isValid, setValid] = useState(false);
    const [isSending, setSending] = useState(false);
    const [isPolicyError, setPolicyError] = useState(false);
    const [is2FACodeConfirm, set2FACodeConfirm] = useState(false);
    const [mfaToken, setMfaToken] = useState<string>('');
    const [url2FASetup, setUrl2FASetup] = useState('');
    const [oneTimePassword, setOneTimePassword] = useState<string>('');
    const [isOpenForgotModal, setOpenForgotModal] = useState(false);
    const [initialValues2FA] = useState({ secretWord: '' });
    // const { hostname } = window.location;
    // const isLogoEnabled = hostname === appConfig.defaultOCSHost;

    const onFieldsChange = (_: FieldData[], allFields: Array<FieldData>): void => {
        setValid(
            (allFields as Array<IValidationField>)
                .filter((f: IValidationField) => validatedFields.includes(f.name[0]))
                .map((f: IValidationField) => f.touched && !f.errors.length)
                .reduce((res: boolean, item: boolean) => res && item),
        );
    };

    const onOneTimePasswordChange = (e: any) => {
        setOneTimePassword(e.target.value);
    };

    const sendLogin = async (trigger2FA = false): Promise<void> => {
        let user = form.getFieldsValue() as ILoginRequest;
        setSending(true);
        setValid(false);

        if ((oneTimePassword && is2FACodeConfirm) || trigger2FA) {
            user = { ...user, totp: oneTimePassword };
        }

        const { resp, isError } = await authLoginUser(user);

        setSending(false);
        setValid(!isError);
        if (isError) {
            await form.validateFields(['password']);
        } else {
            removeUiNotificationByMessage('Please login again.');
            if (resp.mfaToken) {
                const urlResp = await init2FAEnable(user.password, resp.mfaToken);

                if (urlResp?.provisioningUri) {
                    setUrl2FASetup(urlResp.provisioningUri);
                    setMfaToken(resp.mfaToken);
                }
            } else if (resp.totpRequired) {
                set2FACodeConfirm(true);
            } else if (!resp.docs_accepted) {
                await getAuthAcceptanceDocuments(resp.companyId);
                setPolicyError(true);
                setUrl2FASetup('');
                set2FACodeConfirm(false);
            } else {
                await getProfileInfo();
                await setAuthAppStatus(AppStatus.READY);
                set2FACodeConfirm(false);
            }
        }
    };

    const on2FAFieldsChange = (fields: FieldData[]): void => {
        const password = fields.find((f: FieldData) => (f.name as Array<string>)[0] === 'secretWord')?.value;
        const fieldNameChange = fields && fields[0] && (fields[0].name as IAntDFormChangeFieldName);
        if (fieldNameChange) {
            apiMessagesDeleteMessageByFieldName(fieldNameChange);
        }

        setOneTimePassword(password);
    };

    const init2FAEnable = async (password: string, token: string): Promise<IPOSTUser2FAEnableResponses> => {
        const resp = await postUser2FAEnableStep1({ password }, token);

        return resp as IPOSTUser2FAEnableResponses;
    };

    const enable2FA = async () => {
        const isOkEnable = await postUser2FAEnableStep2({ totp: oneTimePassword }, mfaToken);

        if (isOkEnable) {
            addNotification({
                type: NotificationType.INFO,
                message: '2-Step Authentication',
                description: `2-Step Authentication has been successfully enabled`,
                duration: 20,
            });

            await sendLogin(true);
        } else {
            addNotification({
                type: NotificationType.WARNING,
                message: '2-Step Authentication',
                description: `One time password is incorrect. Please try again.`,
                duration: 20,
            });
        }
    };

    // useEffect(() => {
    //     const iframe: HTMLIFrameElement = document.createElement('iframe');
    //     //APPLY STYLES TO IFRAME AS SUITABLE
    //     iframe.width = '100%';
    //     iframe.height = '100%';
    //     iframe.style.position = 'absolute';
    //     iframe.frameBorder = '0';
    //     iframe.scrolling = 'no';
    //     iframe.src = 'about:blank';
    //     //ADD OTHER PROPERTIES OF IFRAME AS SUITABLE
    //
    //     const bodyStyle = `
    //         display: flex;
    //         justify-content: center;
    //         height: 32px;
    //         width: 82px;
    //     `;
    //
    //     const htmlStyle = `
    //         display: flex;
    //         justify-content: center;
    //     `;
    //
    //     const content = `
    //         <html style="${htmlStyle}">
    //             <script type="text/javascript"> //<![CDATA[
    //                 var tlJsHost = ((window.location.protocol == "https:") ? "https://secure.trust-provider.com/" : "http://www.trustlogo.com/");
    //                 document.write(unescape("%3Cscript src='" + tlJsHost + "trustlogo/javascript/trustlogo.js' type='text/javascript'%3E%3C/script%3E"));
    //                 //]]>
    //             </script>
    //             <body style="${bodyStyle}">
    //                 <style type="text/css" scoped>
    //                     #tl_popupSC3 { background-color: white !important; width: 255px !important; height: 205px !important; }
    //                     a {
    //                         width: 100px;
    //                         position: relative;
    //                         opacity: 0;
    //                         bottom: 7px;
    //                     }
    //                 </style>
    //                 <script language="JavaScript" type="text/javascript">
    //                     TrustLogo("https://ocs.geniox.com/static/media/ssl.f092c8d0.png", "SC3", "none");
    //                 </script>
    //
    //             </body>
    //         </html>`;
    //
    //     document.querySelector('#sslSection')?.appendChild(iframe);
    //     iframe?.contentWindow?.document.open('text/html', 'replace');
    //     iframe?.contentWindow?.document.write(content);
    //     iframe?.contentWindow?.document.close();
    // }, []);

    return (
        <Catcher>
            <Form
                form={form}
                name={'Login'}
                className={'formStyle'}
                initialValues={initialValues}
                onFieldsChange={onFieldsChange}
                layout={'vertical'}
            >
                <Form.Item name="email" rules={makeRuleEmail()} label={tF('Your username')}>
                    <Input
                        prefix={<UserOutlined className="site-form-item-icon" />}
                        placeholder={tF('Email')}
                        disabled={isPolicyError}
                    />
                </Form.Item>
                <Form.Item name="password" rules={makeRulePassword()} label={tF('Password')}>
                    <Input.Password
                        prefix={<LockOutlined className="site-form-item-icon" />}
                        type="password"
                        placeholder={tF('Password')}
                        disabled={isPolicyError}
                    />
                </Form.Item>
                <Row>
                    <Col xs={12}>
                        <Form.Item>
                            <Form.Item name="remember" valuePropName="checked" className={Styles.checkField}>
                                <Checkbox disabled={isPolicyError}>{tF('Remember me')}</Checkbox>
                            </Form.Item>
                        </Form.Item>
                    </Col>
                    {!isPolicyError ? (
                        <Col xs={12}>
                            <div className={Styles.forgotLink}>
                                <a href={'#'} onClick={(): void => setOpenForgotModal(true)} data-test="ForgotPassword">
                                    {tF('Forgot Password?')}
                                </a>
                            </div>
                        </Col>
                    ) : null}
                </Row>

                {!isPolicyError ? (
                    <Form.Item>
                        <Button
                            icon={<UnlockOutlined />}
                            type="primary"
                            htmlType="submit"
                            className="login-form-button"
                            data-test="ButtonLogin"
                            block
                            disabled={!isValid}
                            loading={isSending}
                            onClick={() => sendLogin()}
                        >
                            {tB('Log in')}
                        </Button>
                    </Form.Item>
                ) : null}
                {isPolicyError ? <TermsFormFields /> : null}
                <div className={Styles.description}>
                    {tT(
                        'We protect the security of your information during transmission by using Secure Sockets Layer (SSL) software.',
                    )}
                </div>
                {/*{isLogoEnabled ? (*/}
                {/*    <div id="sslSection" className={Styles.sslLogo}>*/}
                {/*        <img alt={'ssl logo'} src={sslImage} />*/}
                {/*    </div>*/}
                {/*) : null}*/}
                <GoGetSslLogo />
                {/*<Button onClick={(): void => set2FACodeConfirm(true)}>Test</Button>*/}
            </Form>
            <Modal
                title={tP('Forgotten Password')}
                open={isOpenForgotModal}
                onCancel={(): void => setOpenForgotModal(false)}
                wrapClassName={'modal'}
                footer={null}
            >
                <ForgotPasswordForm setOpenModal={setOpenForgotModal} />
            </Modal>
            <Modal
                title={'2FA Code Confirm'}
                open={is2FACodeConfirm}
                onCancel={(): void => set2FACodeConfirm(false)}
                wrapClassName={'modal'}
                onOk={() => sendLogin()}
                okText={'Submit Code'}
                centered
                confirmLoading={isSending}
            >
                <Form2FACodeConfirm
                    isLoading={isSending}
                    onOneTimePasswordChange={onOneTimePasswordChange}
                    onSubmit={() => sendLogin()}
                />
            </Modal>
            <Modal
                title={'Enable 2-Step Authentication'}
                open={!!url2FASetup}
                onCancel={(): void => setUrl2FASetup('')}
                wrapClassName={'modal'}
                okText={'Enable'}
                onOk={enable2FA}
                centered
                confirmLoading={isSending}
            >
                <LoadingApiByIds idSpinners={['PATCH_USER_CONFIG_MFA_ENABLE']} />
                <div className={ViewStyles.modalContent2FA}>
                    <Form
                        name={'Form2FA'}
                        className={ViewStyles.form2FA}
                        initialValues={initialValues2FA}
                        onFieldsChange={on2FAFieldsChange}
                        layout={'horizontal'}
                    >
                        <Form2FAQRCode provisioningUri={url2FASetup} />
                    </Form>
                </div>
            </Modal>
        </Catcher>
    );
});
